import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode, useEffect } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { OrdinalNumber } from '@flipster/react/design-utils/ordinal-number';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import CaretUpIcon from '@flipster/react/icon/generated/CaretUpIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';

import { MaxWidthLayout } from './component/layout';
import { toRoiPercent } from './misc/roi';
import { competitionBunja, competitionTabsBunja, myCompetitionInfoBunja } from './state';

const RankChangeTodayBadge = ({ rank, lastRank }: { rank: number; lastRank?: number }) => {
  if (!lastRank || rank === lastRank) return null;

  return (
    <span
      className={hstack({
        py: 4,
        px: 8,
        borderColor: 'border.default.level-1',
        borderWidth: '1px',
        borderStyle: 'solid',
        rounded: 1000,
      })}
    >
      {rank < lastRank ? (
        <CaretUpIcon size={12} color={token('colors.content.state.positive')} />
      ) : (
        <CaretDownIcon size={12} color={token('colors.content.state.negative')} />
      )}
      <Formatted.Number value={lastRank - rank} withColor withPlus css={{ textStyle: 'Text/XS/Regular' }} />
      <span className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2', ml: 2 })}>
        <FormattedMessage id='Autogen_common.r251126.s_Today' />
      </span>
    </span>
  );
};

const MyRankItem = ({ label, rank, lastRank }: { label: ReactNode; rank?: number; lastRank?: number }) => {
  if (!rank) return null;

  return (
    <li className={listStyle}>
      <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>{label}</span>
      <div className={hstack({ gap: 8 })}>
        <OrdinalNumber value={rank} css={{ textStyle: 'Title/XS/Monospace', color: 'content.default.level-5' }} />
        <RankChangeTodayBadge rank={rank} lastRank={lastRank} />
      </div>
    </li>
  );
};

const MyInfoItem = ({ label, value, isPnl }: { label: ReactNode; value: string; isPnl?: boolean }) => {
  return (
    <li className={listStyle}>
      <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>{label}</span>
      <Formatted.Number
        value={value}
        precision={2}
        toFixed={isPnl}
        css={{ textStyle: 'Title/XS/Monospace', color: 'content.default.level-5' }}
        withColor={isPnl}
        withPlus={isPnl}
      />
    </li>
  );
};

export const MyInfo = () => {
  const { myCompetitionInfoDataAtom } = useBunja(myCompetitionInfoBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { refetchMyCompetitionInfo } = useBunja(competitionBunja);
  const myCompetitionInfoData = useAtomValue(myCompetitionInfoDataAtom);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);

  useEffect(() => {
    // 탭 변경 시 내 대회 정보만 리패치
    if (selectedEventTabValue) {
      void refetchMyCompetitionInfo();
    }
  }, [selectedEventTabValue, refetchMyCompetitionInfo]);

  if (!myCompetitionInfoData) return null;

  return (
    <MaxWidthLayout css={{ mb: 128, tabletDown: { mt: 40 } }}>
      <ul
        className={flex({
          flexDirection: { base: 'column', tablet: 'row' },
          py: { base: 0, tablet: 32 },
          px: 16,
          rounded: 16,
          bg: 'background.default.level-1',
          tabletDown: { alignItems: 'center' },
          '& > li + li': {
            pos: 'relative',
            _before: {
              content: '""',
              pos: 'absolute',
              left: 0,
              tablet: {
                top: '50%',
                transform: 'translateY(-50%)',
                w: 1,
                h: 50,
                bg: 'linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, #B4B4B4 50%, rgba(255, 255, 255, 0) 100%)',
              },
              tabletDown: {
                w: '100%',
                h: 1,
                top: 0,
                bg: 'linear-gradient(90deg, rgba(28, 28, 28, 0) 0%, #B4B4B4 50%, rgba(28, 28, 28, 0) 100%)',
              },
            },
          },
        })}
      >
        {myCompetitionInfoData.volume && (
          <>
            <MyInfoItem
              label={<FormattedMessage id='Competition.r251215.My_volume_symbol' values={{ symbol: UsdtCurrency }} />}
              value={myCompetitionInfoData.volume.value}
            />
            <MyRankItem
              label={<FormattedMessage id='Competition.r251215.My_volume_rank' />}
              rank={myCompetitionInfoData.volume.rank}
              lastRank={myCompetitionInfoData.volume.lastRank}
            />
          </>
        )}
        {myCompetitionInfoData.pnl && (
          <>
            <MyInfoItem
              label={<FormattedMessage id='Competition.r251215.My_pnl_symbol' values={{ symbol: UsdtCurrency }} />}
              value={myCompetitionInfoData.pnl.value}
              isPnl
            />
            <MyRankItem
              label={<FormattedMessage id='Competition.r251215.My_pnl_rank' />}
              rank={myCompetitionInfoData.pnl.rank}
              lastRank={myCompetitionInfoData.pnl.lastRank}
            />
          </>
        )}
        {myCompetitionInfoData.roi && (
          <>
            <MyInfoItem
              label={<FormattedMessage id='Tmp.inseok.My_roi_symbol' values={{ symbol: '%' }} />}
              value={toRoiPercent(myCompetitionInfoData.roi.value)}
              isPnl
            />
            <MyRankItem
              label={<FormattedMessage id='Tmp.inseok.My_roi_rank' />}
              rank={myCompetitionInfoData.roi.rank}
              lastRank={myCompetitionInfoData.roi.lastRank}
            />
          </>
        )}
      </ul>
    </MaxWidthLayout>
  );
};

const listStyle = vstack({
  flex: 1,
  gap: 8,
  alignItems: 'center',
  tablet: { px: 48 },
  tabletDown: { w: '100%', py: 16 },
});
